import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CheckCircle,
  Cancel,
  VideoCall,
  Phone,
  CalendarToday,
  Schedule,
  Person
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { Appointment, Patient, Doctor } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios'; // Assuming axios is installed
import { getCacheBustedImageUrl } from '../../utils/s3Utils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`appointments-tabpanel-${index}`}
      aria-labelledby={`appointments-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AppointmentsPage() {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [formData, setFormData] = useState({
    patientId: '',
    date: new Date(),
    time: new Date(),
    type: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({
    doctorId: '',
    patientId: '',
    type: ''
  });
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const location = useLocation();
  const dataGridRef = useRef<any>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const getApi = () => {
    const currentToken = localStorage.getItem('token');
    return axios.create({
      baseURL: API_URL,
      headers: {
        Authorization: `Bearer ${currentToken}`
      }
    });
  };

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      if (!user?.role || !user?.id) {
        console.error('User role or ID not available');
        setLoading(false);
        return;
      }
      // SuperAdmin should see ALL appointments, not just their own
      const endpoint = user.role === 'superadmin'
        ? '/appointments/all'
        : `/appointments/${user.role}/${user.id}`;
      const response = await getApi().get(endpoint);
      console.log('Fetched appointments:', response.data);
      const appointmentsData = response.data;
      if (appointmentsData && Array.isArray(appointmentsData)) {
        const appointmentsWithDateObjects = appointmentsData.map((appointment: any) => {
          const appointmentDateTime = new Date(`${appointment.appointment_date}T${appointment.appointment_time}`);
          return {
            ...appointment,
            date: appointmentDateTime,
            time: appointmentDateTime, // Both date and time fields will be Date objects
            type: appointment.appointment_type, // Map backend's appointment_type to frontend's type
          };
        });

        // Sort appointments by date descending (newest first)
        appointmentsWithDateObjects.sort((a: any, b: any) => b.date.getTime() - a.date.getTime());

        setAppointments(appointmentsWithDateObjects);
      } else {
        console.error('Invalid appointments data received:', appointmentsData);
        setAppointments([]);
      }
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    if (user?.role === 'superadmin') {
      try {
        const response = await getApi().get('/auth/doctors');
        setDoctors(response.data);
      } catch (error) {
        console.error('Failed to fetch doctors:', error);
      }
    }
  };

  const fetchPatientsByDoctor = async (doctorId: string) => {
    if (!doctorId) {
      setPatients([]);
      return;
    }
    setLoadingPatients(true);
    try {
      let response;
      if (user?.role === 'superadmin') {
        response = await getApi().get(`/patients/doctor/${doctorId}/patients`);
      } else if (user?.role === 'doctor') {
        response = await getApi().get(`/patients/?assigned_to_me=true`);
      }
      setPatients(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
      setPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    fetchDoctors();
  }, [user]);

  useEffect(() => {
    if (selectedDoctorId) {
      fetchPatientsByDoctor(selectedDoctorId);
    }
  }, [selectedDoctorId]);

  // Handle highlight from navigation state
  useEffect(() => {
    console.log('🔍 AppointmentsPage - useEffect triggered');
    console.log('  - location.state:', location.state);
    console.log('  - appointments.length:', appointments.length);

    const state = location.state as { highlightId?: string };
    console.log('  - highlightId from state:', state?.highlightId);

    if (state?.highlightId && appointments.length > 0) {
      const targetId = state.highlightId;
      console.log('🎯 Highlighting appointment with ID:', targetId);
      console.log('  - All appointment IDs:', appointments.map(a => a.id));

      // Find the index of the target appointment
      const targetIndex = appointments.findIndex(a => a.id === targetId);
      console.log('  - Target index in appointments array:', targetIndex);

      if (targetIndex === -1) {
        console.error('  - ❌ Appointment not found in array!');
        return;
      }

      // Set highlighted row
      setHighlightedRowId(targetId);
      console.log('  - highlightedRowId set to:', targetId);

      // Calculate which page the row is on (assuming 10 rows per page)
      const pageSize = paginationModel.pageSize;
      const targetPage = Math.floor(targetIndex / pageSize);
      console.log('  - Target is on page:', targetPage, '(index:', targetIndex, ', pageSize:', pageSize, ')');

      // Change to the correct tab if needed (All tab = 0)
      if (tabValue !== 0) {
        console.log('  - Switching to "All" tab');
        setTabValue(0);
      }

      // Change to the correct page
      if (paginationModel.page !== targetPage) {
        console.log('  - Changing from page', paginationModel.page, 'to page', targetPage);
        setPaginationModel({ ...paginationModel, page: targetPage });
      }

      // Wait for DataGrid to render with the correct page, then scroll
      setTimeout(() => {
        console.log('  - Attempting to scroll to element with data-id:', targetId);
        const element = document.querySelector(`[data-id="${targetId}"]`);
        console.log('  - Element found:', element);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          console.log('  - Scroll initiated');
        } else {
          console.error('  - ❌ Element not found! Trying to find all data-id elements...');
          const allDataIds = document.querySelectorAll('[data-id]');
          console.log('  - All data-id elements:', Array.from(allDataIds).map(el => el.getAttribute('data-id')));
          console.log('  - NOTE: The row might be on a different page. Target index:', targetIndex, 'Target page:', targetPage);
        }
      }, 300); // Reduced delay to 500ms for more responsive scrolling

      // Clear highlight after animation completes
      setTimeout(() => {
        console.log('  - Clearing highlight');
        setHighlightedRowId(null);
        // Clear navigation state
        window.history.replaceState({}, document.title);
      }, 10000); // Increased to 10000ms for longer blink visibility
    } else {
      if (!state?.highlightId) {
        console.log('  - ⚠️ No highlightId in state');
      }
      if (appointments.length === 0) {
        console.log('  - ⚠️ Appointments array is empty');
      }
    }
  }, [location.state, appointments, tabValue]);



  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const validateForm = () => {
    const errors = {
      doctorId: '',
      patientId: '',
      type: ''
    };

    if (!selectedDoctorId) {
      errors.doctorId = 'Please select a doctor';
    }
    if (!formData.patientId) {
      errors.patientId = 'Please select a patient';
    }
    if (!formData.type) {
      errors.type = 'Please select appointment type';
    }

    setFormErrors(errors);
    return !Object.values(errors).some(error => error !== '');
  };

  const handleAddAppointment = async () => {
    setSelectedAppointment(null);
    // If user is a doctor, auto-set their ID as the selected doctor
    const doctorId = user?.role === 'doctor' ? user.id : '';
    setSelectedDoctorId(doctorId);
    setPatients([]);
    setFormData({
      patientId: '',
      date: new Date(),
      time: new Date(),
      type: '',
      notes: ''
    });
    setFormErrors({
      doctorId: '',
      patientId: '',
      type: ''
    });

    // For doctors, fetch patients immediately
    if (user?.role === 'doctor' && doctorId) {
      await fetchPatientsByDoctor(doctorId);
    }

    setOpenDialog(true);
  };

  const handleEditAppointment = async (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    const doctorId = (appointment as any).doctor_id || '';
    setSelectedDoctorId(doctorId);

    // The patientId from the appointment object is actually patient_id
    const patientId = (appointment as any).patient_id || '';

    setFormData({
      patientId: patientId,
      date: appointment.date,
      time: appointment.time,
      type: appointment.type,
      notes: appointment.notes || ''
    });
    setFormErrors({
      doctorId: '',
      patientId: '',
      type: ''
    });
    setOpenDialog(true);
  };

  const handleSaveAppointment = async () => {
    if (!validateForm()) {
      return;
    }

    const appointmentData = {
      doctor_id: selectedDoctorId,
      patient_id: formData.patientId,
      user_id: user?.id,
      appointment_date: formData.date.toISOString().split('T')[0],
      appointment_time: formData.time.toTimeString().split(' ')[0],
      appointment_type: formData.type,
      notes: formData.notes
    };

    try {
      let response;
      if (selectedAppointment) {
        response = await getApi().put(`/appointments/${selectedAppointment.id}`, appointmentData);
        setAlertMessage({ type: 'success', message: 'Appointment updated successfully!' });
      } else {
        response = await getApi().post('/appointments/', appointmentData);
        if (response.data.status === 'Success') {
          setAlertMessage({ type: 'success', message: response.data.message || 'Appointment created successfully!' });
        } else {
          setAlertMessage({ type: 'error', message: response.data.message || 'Failed to create appointment' });
          return;
        }
      }
      await fetchAppointments();
      setOpenDialog(false);
      // Clear alert after 5 seconds
      setTimeout(() => setAlertMessage(null), 5000);
    } catch (error: any) {
      console.error('Failed to save appointment:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to save appointment. Please try again.';
      setAlertMessage({ type: 'error', message: errorMessage });
      // Clear alert after 5 seconds
      setTimeout(() => setAlertMessage(null), 5000);
    }
  };

  const handleUpdateStatus = async (appointmentId: string, status: string) => {
    if (window.confirm(`Are you sure you want to mark this appointment as ${status}?`)) {
      try {
        await getApi().patch(`/appointments/${appointmentId}`, { status });
        await fetchAppointments();
        setAlertMessage({ type: 'success', message: `Appointment marked as ${status} successfully!` });
        setTimeout(() => setAlertMessage(null), 5000);
      } catch (error: any) {
        console.error('Failed to update appointment status:', error);
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to update appointment status. Please try again.';
        setAlertMessage({ type: 'error', message: errorMessage });
        setTimeout(() => setAlertMessage(null), 5000);
      }
    }
  };

  const handleDeleteAppointment = async (appointmentId: string) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        await getApi().delete(`/appointments/${appointmentId}`);
        await fetchAppointments();
        setAlertMessage({ type: 'success', message: 'Appointment deleted successfully!' });
        setTimeout(() => setAlertMessage(null), 5000);
      } catch (error: any) {
        console.error('Failed to delete appointment:', error);
        const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to delete appointment. Please try again.';
        setAlertMessage({ type: 'error', message: errorMessage });
        setTimeout(() => setAlertMessage(null), 5000);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'no-show': return 'warning';
      case 'rescheduled': return 'warning';
      default: return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'consultation': return 'info';
      case 'follow-up': return 'success';
      case 'emergency': return 'error';
      case 'reschedule': return 'warning';
      default: return 'default';
    }
  };

  const filterAppointmentsByTab = (appointments: Appointment[] | null | undefined) => {
    if (!appointments) {
      console.warn("Appointments array is null or undefined. Returning an empty array.");
      return [];
    }

    const today = new Date().toISOString().split('T')[0];

    switch (tabValue) {
      case 0: // All
        return appointments;
      case 1: // Today
        return appointments.filter(a => {
          if (!a.date) {
            console.error("Appointment date is null or undefined:", a);
            return false; // Skip this appointment
          }
          try {
            return a.date.toISOString().split('T')[0] === today;
          } catch (error) {
            console.error("Error converting date to string:", a.date, error);
            return false; // Skip this appointment
          }
        });
      case 2: // Upcoming
        return appointments.filter(a => {
          if (!a.date) {
            console.error("Appointment date is null or undefined:", a);
            return false; // Skip this appointment
          }
          try {
            return a.date.toISOString().split('T')[0] > today && a.status === 'scheduled';
          } catch (error) {
            console.error("Error converting date to string:", a.date, error);
            return false; // Skip this appointment
          }
        });
      case 3: // Completed
        return appointments.filter(a => a.status === 'completed');
      case 4: // Rescheduled
        return appointments.filter(a => a.status === 'rescheduled');
      default:
        return appointments;
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'patientName',
      headerName: 'Patient',
      width: 200,
      renderCell: (params) => {
        const patient = patients.find(p => p.id === params.row.patient_id);
        const profileImage = getCacheBustedImageUrl(params.row.profile_image || patient?.profile_image);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              sx={{ width: 32, height: 32 }}
              src={profileImage}
            >
              {(patient?.name || params.value)?.charAt(0) || '?'}
            </Avatar>
            {patient?.name || (params.value ? params.value.split(' - ')[0] : 'Unknown Patient')}
          </Box>
        );
      }
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 120,
      renderCell: (params) => {
        const date = params.value;
        return date instanceof Date && !isNaN(date.getTime()) ? date.toLocaleDateString() : 'N/A';
      }
    },
    {
      field: 'time',
      headerName: 'Time',
      width: 100,
      renderCell: (params) => {
        const time = params.value;
        return time instanceof Date && !isNaN(time.getTime()) ? time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A';
      }
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getTypeColor(params.value) as any}
          size="small"
          variant="outlined"
        />
      )
    },
    // {
    //   field: 'status',
    //   headerName: 'Status',
    //   width: 120,
    //   renderCell: (params) => (
    //     <Chip
    //       label={params.value}
    //       color={getStatusColor(params.value) as any}
    //       size="small"
    //     />
    //   )
    // },
    {
      field: 'notes',
      headerName: 'Notes',
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.value || 'No notes'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit Appointment">
            <IconButton
              size="small"
              onClick={() => handleEditAppointment(params.row)}
            >
              <Edit />
            </IconButton>
          </Tooltip>

          {params.row.status === 'scheduled' && (
            <>
              <Tooltip title="Mark Complete">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleUpdateStatus(params.row.id, 'completed')}
                >
                  <CheckCircle />
                </IconButton>
              </Tooltip>

              {/* <Tooltip title="Reschedule">
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => handleUpdateStatus(params.row.id, 'rescheduled')}
                >
                  <Schedule />
                </IconButton>
              </Tooltip> */}

              {/* <Tooltip title="Cancel">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleUpdateStatus(params.row.id, 'cancelled')}
                >
                  <Cancel />
                </IconButton>
              </Tooltip> */}

              {/* <Tooltip title="Video Call">
                <IconButton size="small" color="primary">
                  <VideoCall />
                </IconButton>
              </Tooltip> */}

              {/* <Tooltip title="Phone Call">
                <IconButton size="small" color="info">
                  <Phone />
                </IconButton>
              </Tooltip> */}
            </>
          )}

          {params.row.status === 'rescheduled' && (
            <Tooltip title="Mark Complete">
              <IconButton
                size="small"
                color="success"
                onClick={() => handleUpdateStatus(params.row.id, 'completed')}
              >
                <CheckCircle />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteAppointment(params.row.id)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const filteredAppointments = filterAppointmentsByTab(appointments).filter(Boolean); // Filter out null/undefined
  console.log('Filtered appointments for DataGrid:', filteredAppointments);
  const todayAppointments = appointments.filter(a => a.date && a.date.toISOString().split('T')[0] === new Date().toISOString().split('T')[0]);
  const upcomingAppointments = appointments.filter(a => a.date && a.date.toISOString().split('T')[0] > new Date().toISOString().split('T')[0] && a.status === 'scheduled');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            Appointment Management
          </Typography>
          {(user?.role === 'superadmin' || user?.role === 'doctor') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddAppointment}
            >
              Schedule Appointment
            </Button>
          )}
        </Box>

        {todayAppointments.length > 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            You have {todayAppointments.length} appointment(s) scheduled for today.
          </Alert>
        )}

        {alertMessage && (
          <Alert
            severity={alertMessage.type}
            sx={{ mb: 3 }}
            onClose={() => setAlertMessage(null)}
          >
            {alertMessage.message}
          </Alert>
        )}

        {/* Stats Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 3,
            mb: 4
          }}
        >
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday color="primary" />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Today's Appointments
                  </Typography>
                  <Typography variant="h4">
                    {todayAppointments.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule color="info" />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Upcoming
                  </Typography>
                  <Typography variant="h4">
                    {upcomingAppointments.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Completed
                  </Typography>
                  <Typography variant="h4">
                    {appointments.filter(a => a.status === 'completed').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person color="primary" />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Appointments
                  </Typography>
                  <Typography variant="h4">
                    {appointments.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Appointments Table with Tabs */}
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={`All (${appointments.length})`} />
              <Tab label={`Today (${todayAppointments.length})`} />
              <Tab label={`Upcoming (${upcomingAppointments.length})`} />
              {/* <Tab label={`Completed (${appointments.filter(a => a.status === 'completed').length})`} />
              <Tab label={`Rescheduled (${appointments.filter(a => a.status === 'rescheduled').length})`} /> */}
            </Tabs>
          </Box>

          <CardContent>
            <style>
              {`
                @keyframes blueBlinkAnimation {
                  0%, 100% { background-color: transparent; }
                  25% { background-color: rgba(33, 150, 243, 0.3); }
                  50% { background-color: rgba(33, 150, 243, 0.5); }
                  75% { background-color: rgba(33, 150, 243, 0.3); }
                }
                .highlighted-row {
                  animation: blueBlinkAnimation 4s ease-in-out;
                }
              `}
            </style>
            <DataGrid
              ref={dataGridRef}
              rows={filteredAppointments}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50]}
              checkboxSelection={false}
              disableRowSelectionOnClick
              autoHeight
              getRowClassName={(params) =>
                params.row.id === highlightedRowId ? 'highlighted-row' : ''
              }
              sx={{
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            />
          </CardContent>
        </Card>

        {/* Add/Edit Appointment Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {selectedAppointment ? 'Edit Appointment' : 'Schedule New Appointment'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{
              display: 'grid',
              gap: 2,
              mt: 1,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
            }}>
              {user?.role === 'superadmin' && (
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <FormControl fullWidth required error={!!formErrors.doctorId}>
                    <InputLabel>Doctor</InputLabel>
                    <Select
                      value={selectedDoctorId}
                      onChange={(e) => {
                        setSelectedDoctorId(e.target.value);
                        setFormData({ ...formData, patientId: '' }); // Reset patient on doctor change
                        setFormErrors({ ...formErrors, doctorId: '' });
                      }}
                    >
                      {doctors.map((doctor) => (
                        <MenuItem key={doctor.id} value={doctor.id}>
                          {doctor.full_name || doctor.username}
                        </MenuItem>
                      ))}
                    </Select>
                    {formErrors.doctorId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {formErrors.doctorId}
                      </Typography>
                    )}
                  </FormControl>
                </Box>
              )}

              <Box sx={{ gridColumn: '1 / -1' }}>
                <FormControl fullWidth required disabled={!selectedDoctorId || loadingPatients} error={!!formErrors.patientId}>
                  <InputLabel>Patient</InputLabel>
                  <Select
                    value={formData.patientId}
                    onChange={(e) => {
                      setFormData({ ...formData, patientId: e.target.value });
                      setFormErrors({ ...formErrors, patientId: '' });
                    }}
                    startAdornment={loadingPatients ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                  >
                    {patients.map((patient) => {
                      const profileImage = getCacheBustedImageUrl(patient.profile_image);
                      return (
                        <MenuItem key={patient.id} value={patient.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{ width: 24, height: 24 }}
                              src={profileImage}
                            >
                              {patient.name.charAt(0)}
                            </Avatar>
                            {patient.name} - {patient.email}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {formErrors.patientId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {formErrors.patientId}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box>
                <DatePicker
                  label="Appointment Date"
                  value={formData.date}
                  onChange={(newValue) => setFormData({ ...formData, date: newValue || new Date() })}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </Box>

              <Box>
                <TimePicker
                  label="Appointment Time"
                  value={formData.time}
                  onChange={(newValue) => setFormData({ ...formData, time: newValue || new Date() })}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </Box>

              <Box sx={{ gridColumn: '1 / -1' }}>
                <FormControl fullWidth required error={!!formErrors.type}>
                  <InputLabel>Appointment Type</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => {
                      setFormData({ ...formData, type: e.target.value });
                      setFormErrors({ ...formErrors, type: '' });
                    }}
                  >
                    <MenuItem value="consultation">Consultation</MenuItem>
                    <MenuItem value="follow-up">Follow-up</MenuItem>
                    <MenuItem value="emergency">Emergency</MenuItem>
                    <MenuItem value="reschedule">Reschedule</MenuItem>
                  </Select>
                  {formErrors.type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {formErrors.type}
                    </Typography>
                  )}
                </FormControl>
              </Box>

              <Box sx={{ gridColumn: '1 / -1' }}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes for the appointment..."
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveAppointment} variant="contained">
              {selectedAppointment ? 'Update' : 'Schedule'} Appointment
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}
